import { useEffect, useState } from 'react'
import { create } from 'zustand'
import { Link, useMatchRoute } from '@tanstack/react-router'
import { useQuery } from 'convex/react'
import { api } from '@convex/_generated/api'
import type { Id } from '@convex/_generated/dataModel'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { isBareShortcut } from '@/lib/hotkeys'

/** The key that opens it, matching `/` everywhere else on the web. */
export const SEARCH_KEY = '/'

/**
 * Finding something by name, from anywhere.
 *
 * There was no search at all. With a flat Tasks list, an archive that had no
 * view until recently and a Someday pile that lives inside the weekly review,
 * anything older than the current screen was effectively unfindable.
 *
 * A dialog on a keystroke rather than a destination, for the same reason
 * capture is: the navigation already holds ten items and does not fit a phone,
 * and search is something you reach for mid-thought rather than somewhere you
 * go.
 *
 * Results say what kind of thing each match is and where it lives, and link
 * there. They deliberately do not link to the item itself — there is no
 * per-item route to link to, and inventing one here would be a bigger change
 * hiding inside a smaller one.
 */

/** Where each kind of thing can be found, since results cannot deep-link. */
const HOME: Record<string, { to: string; label: string }> = {
  task: { to: '/tasks', label: 'Tasks' },
  chore: { to: '/chores', label: 'Chores' },
  habit: { to: '/habits', label: 'Habits' },
  inbox: { to: '/inbox', label: 'Inbox' },
  routine: { to: '/routines', label: 'Routines' },
  list: { to: '/lists', label: 'Lists' },
}

/**
 * Whether the dialog is open, in a store rather than in the dialog.
 *
 * It used to be local state reachable only by pressing "/", and the component
 * renders nothing while closed — so on a phone, which has no "/" to press and
 * showed no control anywhere, search was not merely hard to find. It could not
 * be opened at all.
 */
export const useSearchOpen = create<{
  open: boolean
  setOpen: (open: boolean) => void
}>((set) => ({ open: false, setOpen: (open) => set({ open }) }))

/**
 * The shell: the hotkey, the scrim, and nothing else.
 *
 * What you typed and how wide you searched live in the panel, which only
 * exists while the dialog is open — so both reset by unmounting rather than by
 * an effect that watches `open` and clears them.
 */
export function SearchDialog() {
  const open = useSearchOpen((state) => state.open)
  const setOpen = useSearchOpen((state) => state.setOpen)

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (open && event.key === 'Escape') {
        setOpen(false)
        return
      }
      if (!open && isBareShortcut(event, SEARCH_KEY)) {
        // Otherwise the slash that opened it is also typed into the field.
        event.preventDefault()
        setOpen(true)
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open, setOpen])

  if (!open) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Search"
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/20 px-4 pt-[15vh]"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) setOpen(false)
      }}
    >
      <SearchPanel close={() => setOpen(false)} />
    </div>
  )
}

function SearchPanel({ close }: { close: () => void }) {
  const [text, setText] = useState('')
  const [everywhere, setEverywhere] = useState(false)

  /*
   * Searching from inside a list starts inside that list.
   *
   * You were already looking at it, so "the invoice one" almost always means
   * the one in here. Never a trap, though: the scope says which list it is in
   * and gives it up in one click, and any search that finds nothing here says
   * so rather than looking like the thing does not exist.
   */
  const inList = useMatchRoute()({ to: '/lists/$listId' })
  const scope =
    everywhere || inList === false ? undefined : (inList.listId as Id<'lists'>)

  const results = useQuery(api.search.find, {
    text,
    ...(scope !== undefined ? { listId: scope } : {}),
  })
  const listName = useQuery(
    api.lists.detail,
    scope !== undefined ? { listId: scope } : 'skip',
  )?.name

  return (
    <div className="bg-background w-full max-w-lg rounded-lg p-3 shadow-lg">
      <Input
        autoFocus
        value={text}
        onChange={(event) => setText(event.target.value)}
        placeholder={
          scope === undefined ? 'Find anything by name' : 'Find in this list'
        }
        aria-label="Search"
        className="min-h-11 sm:min-h-9"
      />

      {scope !== undefined && (
        <p className="text-muted-foreground mt-2 flex items-center gap-2 text-xs">
          <span className="flex-1">In {listName ?? 'this list'}</span>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="min-h-8 shrink-0 text-xs"
            onClick={() => setEverywhere(true)}
          >
            Search everywhere
          </Button>
        </p>
      )}

      {results !== undefined && results.length > 0 && (
        <ul className="mt-2 max-h-[50vh] overflow-y-auto">
          {results.map((result) => {
            const home = HOME[result.kind]
            /*
             * A list result opens that list, not the index.
             *
             * Everything else still goes to the screen its kind lives on —
             * there is no per-item route to link a task to, and inventing
             * one here would be a bigger change hiding inside a smaller one.
             */
            const opensList =
              result.kind === 'list' && result.listId !== undefined
                ? result.listId
                : undefined
            return (
              <li key={result._id}>
                <Link
                  to={
                    opensList !== undefined
                      ? '/lists/$listId'
                      : (home?.to ?? '/')
                  }
                  params={opensList !== undefined ? { listId: opensList } : {}}
                  onClick={close}
                  className="hover:bg-muted flex min-h-11 items-center gap-3 rounded-md px-2 sm:min-h-9"
                >
                  <span className="flex-1 leading-snug">{result.title}</span>
                  {result.archived && (
                    // The most common reason something cannot be found is
                    // that it was put away, so say so rather than showing a
                    // result that looks live.
                    <span className="text-muted-foreground text-xs">
                      archived
                    </span>
                  )}
                  <span className="text-muted-foreground text-xs">
                    {result.archived
                      ? 'Archive'
                      : // Which list it is in beats which kind of thing it
                        // is: the kind is visible from the title, the list
                        // is the thing you were trying to remember.
                        (result.listName ?? home?.label ?? '')}
                  </span>
                </Link>
              </li>
            )
          })}
        </ul>
      )}

      <p className="text-muted-foreground mt-2 text-xs">
        {text.trim().length < 2
          ? 'Type at least two letters · Esc to close'
          : results !== undefined && results.length === 0
            ? scope === undefined
              ? 'Nothing matches that.'
              : 'Nothing in this list matches that.'
            : 'Esc to close'}
      </p>
    </div>
  )
}
